import { Radio, Trophy, Users } from "lucide-react";
import { formatPot, type LmsComp } from "../../lib/lms";
import { Countdown } from "./Deadline";
import { ShareStandings } from "./ShareStandings";

const WINDOW_LABEL = { pending: "Picks not open yet", open: "Picks open", closed: "Picks locked — awaiting results" };
const WINDOW_TONE = { pending: "bg-white/15 text-white", open: "bg-[#35E07A] text-[#0B1F33] animate-pulse", closed: "bg-[#FF5D1F] text-white" };

export function PotHero({ comp, live }: { comp: LmsComp; live: boolean }) {
  const alive = comp.players.filter((p) => p.alive).length;
  return (
    <section className="lms-pitch relative overflow-hidden rounded-[22px] border-[3px] border-[#0B1F33] p-4 text-white shadow-[0_5px_0_#0B1F33] sm:p-5" data-testid="lms-pot-hero">
      <div className="relative flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-[0.2em] text-[#FFE14D]">
            <Trophy className="h-4 w-4" strokeWidth={3} /> Last Man Standing
            {live && <span className="ml-1 flex items-center gap-1 rounded-full bg-black/30 px-2 py-0.5 text-[10px] text-[#35E07A]" data-testid="lms-live-indicator"><Radio className="h-3 w-3" /> Live</span>}
          </p>
          <h1 className="ua-title mt-1 truncate text-3xl sm:text-5xl" data-testid="lms-comp-name">{comp.name}</h1>
          <div className="mt-2 flex flex-wrap gap-1.5 text-xs font-extrabold uppercase">
            <span className="rounded-full border-2 border-white/70 px-2.5 py-1" data-testid="lms-gameweek">GW {comp.status === "finished" ? comp.history.length : comp.gameweek}</span>
            {comp.status === "active" && <span className={`rounded-full px-2.5 py-1 ${WINDOW_TONE[comp.window]}`} data-testid="lms-window-status">{WINDOW_LABEL[comp.window]}</span>}
            <span className="flex items-center gap-1 rounded-full bg-black/30 px-2.5 py-1" data-testid="lms-alive-count"><Users className="h-3.5 w-3.5" /> {alive} of {comp.players.length} still in</span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {comp.status === "active" && comp.window === "open" && comp.deadline && <Countdown deadline={comp.deadline} serverNow={comp.server_now} />}
            {comp.players.length > 0 && <ShareStandings comp={comp} />}
          </div>
        </div>
        <div className="rounded-[18px] border-[3px] border-[#0B1F33] bg-[#FFE14D] px-4 py-2 text-center text-[#0B1F33] shadow-[0_4px_0_#0B1F33] -rotate-2" data-testid="lms-prize-pot">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.2em]">Prize pot</p>
          <p className="font-comic text-4xl leading-none sm:text-5xl" data-testid="lms-prize-pot-amount">{formatPot(comp.prize_pot)}</p>
          {comp.rollovers > 0 && <p className="mt-0.5 text-[10px] font-extrabold uppercase" data-testid="lms-rollover-note">Rolled over ×{comp.rollovers}</p>}
        </div>
      </div>
    </section>
  );
}
